'use client';

import React, { useEffect, useMemo, useState } from 'react';

type ConsentCategory = 'functional' | 'analytics' | 'marketing';

type ConsentState = Record<ConsentCategory, boolean>;

const STORAGE_KEY = 'wto_cookie_consent_v1';
const COOKIE_NAME = 'wto_cookie_consent';
const CONSENT_MAX_AGE = 60 * 60 * 24 * 180;

const defaultConsent: ConsentState = {
  functional: false,
  analytics: false,
  marketing: false,
};

const categoryCopy: Array<{
  id: ConsentCategory;
  title: string;
  text: string;
}> = [
  {
    id: 'functional',
    title: 'Functional cookies',
    text: 'Remember choices such as cookie preferences and improve basic website features.',
  },
  {
    id: 'analytics',
    title: 'Analytics cookies',
    text: 'Help us understand visits and improve the website. These stay off unless you allow them.',
  },
  {
    id: 'marketing',
    title: 'Marketing cookies',
    text: 'May be used for social media and advertising measurement. These stay off unless you allow them.',
  },
];

const serializeConsent = (consent: ConsentState) =>
  JSON.stringify({
    essential: true,
    ...consent,
    updatedAt: new Date().toISOString(),
  });

const setConsentCookie = (consent: ConsentState) => {
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(serializeConsent(consent))}; Max-Age=${CONSENT_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
};

const readStoredConsent = (): ConsentState | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return {
      functional: Boolean(parsed.functional),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    return null;
  }
};

const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [choices, setChoices] = useState<ConsentState>(defaultConsent);

  const allEnabled = useMemo<ConsentState>(
    () => ({ functional: true, analytics: true, marketing: true }),
    []
  );

  useEffect(() => {
    const stored = readStoredConsent();
    if (stored) {
      setChoices(stored);
      setConsentCookie(stored);
      return;
    }
    setVisible(true);
  }, []);

  useEffect(() => {
    const openSettings = () => {
      const stored = readStoredConsent();
      setChoices(stored ?? defaultConsent);
      setCustomize(true);
      setVisible(true);
    };

    window.addEventListener('wto:open-cookie-settings', openSettings);
    return () => window.removeEventListener('wto:open-cookie-settings', openSettings);
  }, []);

  const saveConsent = (consent: ConsentState) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
    setConsentCookie(consent);
    setChoices(consent);
    setVisible(false);
    setCustomize(false);
    window.dispatchEvent(new CustomEvent('wto:cookie-consent-updated', { detail: consent }));
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[100] px-4 pb-4 sm:px-6 sm:pb-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-consent-title"
    >
      <div
        className="mx-auto max-w-4xl rounded-2xl p-5 shadow-2xl sm:p-6"
        style={{
          background: '#FFFFFF',
          border: '1px solid rgba(15,35,64,0.14)',
          boxShadow: '0 24px 80px rgba(10,21,53,0.28)',
        }}
      >
        <div className="flex flex-col gap-5 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#1B6B8A' }}>
              Privacy preferences
            </p>
            <h2 id="cookie-consent-title" className="mt-2 font-display text-2xl" style={{ color: '#0F2340' }}>
              Cookies on this website
            </h2>
            <p className="mt-3 text-sm leading-relaxed" style={{ color: '#3A5070' }}>
              We use essential cookies to keep the website secure and working. Optional cookies are used only with your consent. You can accept all, reject non-essential cookies, or choose categories.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:min-w-56">
            <button
              type="button"
              onClick={() => saveConsent(allEnabled)}
              className="rounded-full px-5 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ background: '#1A5FAB', color: '#FFFFFF' }}
            >
              Accept all
            </button>
            <button
              type="button"
              onClick={() => saveConsent(defaultConsent)}
              className="rounded-full px-5 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ background: '#EEF3F8', color: '#0F2340', border: '1px solid rgba(15,35,64,0.12)' }}
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={() => setCustomize((value) => !value)}
              className="rounded-full px-5 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
              style={{ background: 'transparent', color: '#1B6B8A', border: '1px solid rgba(27,107,138,0.28)' }}
              aria-expanded={customize}
            >
              Cookie settings
            </button>
          </div>
        </div>

        {customize && (
          <div className="mt-5 border-t pt-5" style={{ borderColor: 'rgba(15,35,64,0.1)' }}>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-4 rounded-xl p-4" style={{ background: '#F4F6F9' }}>
                <div>
                  <p className="text-sm font-semibold" style={{ color: '#0F2340' }}>Essential cookies</p>
                  <p className="mt-1 text-xs leading-relaxed" style={{ color: '#5C708B' }}>
                    Required for security, forms, administration access and the basic operation of the site. These cannot be disabled.
                  </p>
                </div>
                <span className="rounded-full px-3 py-1 text-xs font-semibold" style={{ background: '#DCEAF7', color: '#0F2340' }}>
                  Always on
                </span>
              </div>

              {categoryCopy.map((category) => (
                <label
                  key={category.id}
                  className="flex cursor-pointer items-start justify-between gap-4 rounded-xl p-4"
                  style={{ background: '#FFFFFF', border: '1px solid rgba(15,35,64,0.1)' }}
                >
                  <span>
                    <span className="block text-sm font-semibold" style={{ color: '#0F2340' }}>{category.title}</span>
                    <span className="mt-1 block text-xs leading-relaxed" style={{ color: '#5C708B' }}>{category.text}</span>
                  </span>
                  <input
                    type="checkbox"
                    checked={choices[category.id]}
                    onChange={(event) =>
                      setChoices((current) => ({
                        ...current,
                        [category.id]: event.target.checked,
                      }))
                    }
                    className="mt-1 h-5 w-5"
                    aria-label={category.title}
                  />
                </label>
              ))}
            </div>

            <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={() => saveConsent(choices)}
                className="rounded-full px-5 py-3 text-sm font-semibold transition-opacity hover:opacity-90"
                style={{ background: '#0F2340', color: '#FFFFFF' }}
              >
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
